import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router'
import { api, ApiError, type Me } from './lib/api'
import { AuthScreen } from './features/auth/AuthScreen'
import { ConnexionRoute } from './features/auth/ConnexionRoute'
import { SpacesScreen } from './features/spaces/SpacesScreen'
import { SpacePage } from './features/spaces/SpacePage'
import { TodayScreen } from './features/items/TodayScreen'
import { RecentsScreen } from './features/items/RecentsScreen'
import { ItemPage } from './features/items/ItemPage'
import { AgendaScreen } from './features/agenda/AgendaScreen'
import { ShoppingScreen } from './features/shopping/ShoppingScreen'
import { AdminScreen } from './features/admin/AdminScreen'

import { Foundations } from './Foundations'
import { SearchScreen } from './features/search/SearchScreen'
import { NewsScreen } from './features/news/NewsScreen'
import { TriageScreen } from './features/triage/TriageScreen'
import { GiftsScreen } from './features/gifts/GiftsScreen'
import { GalleryScreen } from './features/gallery/GalleryScreen'
import { ChangementImpose } from './features/auth/ChangementImpose'
import { ExpensesScreen } from './features/expenses/ExpensesScreen'
import { ToolsScreen } from './features/tools/ToolsScreen'
import { CodeConfidentielPage } from './features/items/CodeConfidentielPage'
import { PartageRoute } from './features/items/PartageRoute'
import { TrashScreen } from './features/trash/TrashScreen'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Décision O3 : pas de temps réel en v1.0 — on rafraîchit au retour au premier plan et
      // toutes les 60 s tant que l'onglet est visible.
      refetchOnWindowFocus: true,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      staleTime: 30_000,
      retry: (failureCount, error) =>
        // Une réponse 401 ou 400 ne s'améliore pas en réessayant.
        error instanceof ApiError && error.status < 500 ? false : failureCount < 2,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/espaces" element={<Home liste />} />
          <Route path="/espace/:spaceId" element={<SpacePage />} />
          <Route path="/espace/:spaceId/recents" element={<RecentsScreen />} />
          <Route path="/espace/:spaceId/a-classer" element={<TriageScreen />} />
          <Route path="/espace/:spaceId/agenda" element={<AgendaScreen />} />
          <Route path="/espace/:spaceId/courses" element={<ShoppingScreen />} />
          <Route path="/espace/:spaceId/cadeaux" element={<GiftsScreen />} />
          <Route path="/espace/:spaceId/galerie" element={<GalleryScreen />} />
          <Route path="/espace/:spaceId/depenses" element={<ExpensesScreen />} />
          <Route path="/espace/:spaceId/outils" element={<ToolsScreen />} />
          <Route path="/espace/:spaceId/corbeille" element={<TrashScreen />} />
          <Route path="/espace/:spaceId/fiche/:itemId" element={<ItemPage />} />
          {/* Hors de l'espace : le code est propre à la personne, pas à l'espace où elle
              protège une fiche. */}
          <Route path="/code-confidentiel" element={<CodeConfidentielPage />} />
          {/* Là où Android dépose ce qu'on partage vers l'application (décision L130). */}
          <Route path="/partage" element={<PartageRoute />} />
          <Route path="/recherche" element={<SearchScreen />} />
          <Route path="/nouveautes" element={<NewsScreen />} />
          <Route path="/connexion" element={<ConnexionRoute />} />
          <Route path="/admin" element={<Admin />} />
          {/* Page technique du socle, conservée hors du parcours produit. */}
          <Route path="/fondations" element={<Foundations />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

/**
 * Console d'exploitation. Ce n'est pas une porte séparée : il faut être connecté à
 * l'application, et le rôle décide de ce qu'on y voit. Un visiteur non connecté retombe sur
 * l'écran de connexion habituel.
 */
function Admin() {
  const { data, isPending, isError } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me })

  if (isPending) {
    return null
  }

  if (isError || !data) {
    return <AuthScreen />
  }

  // Même barrière que sur le reste : l'administration n'est pas une porte de côté.
  return data.mustChangePassword ? <ChangementImpose me={data} /> : <AdminScreen me={data} />
}

function Home({ liste = false }: { liste?: boolean }) {
  const { data, isPending, isError } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: api.me,
  })

  if (isPending) {
    // Squelette plutôt qu'une roue (section 03.6) : même géométrie que l'écran qui suivra.
    return (
      <main
        className="mx-auto flex min-h-dvh flex-col justify-center bg-surface"
        style={{ maxWidth: 'var(--layout-column)', paddingInline: 'var(--layout-gutter)' }}
      >
        <div className="bg-sunken" style={{ height: 38, borderRadius: 8 }} />
        <div className="mt-4 bg-sunken" style={{ height: 17, width: '70%', borderRadius: 8 }} />
      </main>
    )
  }

  if (isError || !data) {
    return <AuthScreen />
  }

  // Barrière avant tout le reste : le mot de passe posé par l'administration est passé par un
  // courrier, donc par une boîte que l'application ne maîtrise pas. Le proposer en option
  // reviendrait à ce que personne ne le fasse.
  if (data.mustChangePassword) {
    return <ChangementImpose me={data} />
  }

  // Carte de navigation de la section 06 : « Onglet 1 — Aujourd'hui ». C'est l'accueil, et la
  // liste des espaces n'est atteinte que par son propre onglet.
  if (!liste) {
    return <TodayScreen activeSpaceId={data.activeSpaceId} />
  }

  return <SpacesScreen me={data} />
}
